import {
  Background,
  Controls,
  Edge,
  MiniMap,
  Node,
  Panel,
  ReactFlow,
  ReactFlowInstance,
  useEdgesState,
  useNodesState,
} from '@xyflow/react';
import {
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  Mail,
  Pause,
} from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getAttributesRequest } from 'store/attributes';
import { getSegmentsRequest } from 'store/segments';
import { v4 as uuidv4 } from 'uuid';
import CampaignDashboard from '../../components/workflow/CampaignDashboard';
import CustomEdge from '../../components/workflow/CustomEdge';
import EditCampaignModal from '../../components/workflow/EditCampaignModal';
import {
  DelayPanel,
  EmailPanel,
  TrueFalsePanel,
} from '../../components/workflow/nodes';
import CenterPanel from '../../components/workflow/Panels/centerPanel/centerPanel';
import LeftSidePanel from '../../components/workflow/Panels/leftSidePanel/LeftSidePanel';
import RightSidePanel from '../../components/workflow/Panels/rightSidePanel/rightSidePanel';
import ReviewCampaignModal from '../../components/workflow/ReviewCampaignModal';
import WorkflowErrorBoundary from '../../components/workflow/WorkflowErrorBoundary';
import WorkflowNode from '../../components/workflow/WorkflowNode';
import { useToast } from '../../contexts/ToastContext';
import { WorkflowCanvasSkeleton } from '../../skeletons';
import { RootState } from '../../store';
import {
  clearCampaignState,
  clearPublishCampaignState,
  getCampaignRequest,
  publishCampaignRequest,
  reviewCampaignRequest,
  updateCampaignRequest,
} from '../../store/campaigns';
import {
  selectPublishCampaignLoading,
  selectPublishCampaignSuccess,
  selectReviewCampaignLoading,
  selectReviews,
} from '../../store/campaigns/campaigns.selector';
import {
  getWorkflowByCampaignRequest,
  selectPublishWorkflowLoading,
  selectSelectedWorkflow,
  selectUpdateWorkflowLoading,
  selectWorkflowsError,
  updateWorkflowRequest,
  clearWorkflowState,
} from '../../store/workflows';
import { cn } from '../../utils/cn';
import {
  getLayoutedElements,
  getReachableDescendantIds,
  nodeHasMergeIncomingBranches,
} from '../../utils/workflowLayout';
import ReviewErrorsPanel from '../../components/workflow/Panels/reviewErrorsPanel/reviewErrorsPanel';

const nodeTypes = {
  action: WorkflowNode,
  trigger: WorkflowNode,
};

// Create default nodes
const createDefaultNodes = () => {
  const triggerNode: Node = {
    id: uuidv4(),
    type: 'trigger',
    position: { x: 0, y: 0 },
    data: {
      status: 'configured',
      type: 'trigger',
      isFixed: true,
      isDefault: true,
    },
    draggable: false,
    deletable: false,
  };
  return { triggerNode };
};

interface WorkflowCanvasProps {
  className?: string;
}

const WorkflowCanvas: React.FC<WorkflowCanvasProps> = ({ className }) => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<any>();
  const campaign = useSelector(
    (state: RootState) => state.campaigns.selectedCampaign
  );
  const loading = useSelector((state: RootState) => state.campaigns.loading);
  const error = useSelector((state: RootState) => state.campaigns.error);
  const success = useSelector((state: RootState) => state.campaigns.success);
  const workflow = useSelector(selectSelectedWorkflow);
  const updateLoading = useSelector(selectUpdateWorkflowLoading);
  const publishLoading = useSelector(selectPublishWorkflowLoading);
  const publishCampaignLoading = useSelector(selectPublishCampaignLoading);
  const publishSuccess = useSelector(selectPublishCampaignSuccess);
  const workflowError = useSelector(selectWorkflowsError);
  const [reviewPanelMode, setReviewPanelMode] = useState<
    'errors' | 'warnings' | null
  >(null);
  const reviewLoading = useSelector(selectReviewCampaignLoading);
  const reviews = useSelector(selectReviews);
  const { showToast } = useToast();
  // State for managing view modes
  const [viewMode, setViewMode] = useState<'dashboard' | 'editor'>('dashboard');

  useEffect(() => {
    if (id) {
      dispatch(getCampaignRequest(id));
      dispatch(getAttributesRequest());
      dispatch(getSegmentsRequest({}));
      dispatch(getWorkflowByCampaignRequest(id));
      dispatch(reviewCampaignRequest(id));
    }
  }, [id, dispatch]);

  // Determine initial view mode based on campaign status
  useEffect(() => {
    if (campaign) {
      if (campaign.status === 'live') {
        setViewMode('dashboard');
      } else {
        setViewMode('editor');
      }
    }
  }, [campaign]);

  useEffect(() => {
    if (success) {
      showToast({
        type: 'success',
        title: 'Success',
        message: 'Campaign updated successfully!',
      });
      dispatch(clearCampaignState());
      setIsEditModalOpen(false);
    }
  }, [success, showToast, dispatch]);

  // Handle publish success and refresh campaign data
  useEffect(() => {
    if (publishSuccess && id) {
      showToast({
        type: 'success',
        title: 'Campaign is live now!',
        message: 'Campaign is live now!',
      });
      // Refresh the campaign data to get the updated status
      dispatch(getCampaignRequest(id));
      dispatch(clearPublishCampaignState());
      // Switch to dashboard view after publishing
      setViewMode('dashboard');
    }
  }, [publishSuccess, id, dispatch, showToast]);

  const { triggerNode } = useMemo(() => createDefaultNodes(), []);

  // Initialize with default nodes
  const [nodes, setNodes, onNodesChange] = useNodesState([triggerNode]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([] as Edge[]);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance<
    Node,
    Edge
  > | null>(null);
  const nodesRef = useRef<Node[]>([]);
  const edgesRef = useRef<Edge[]>([]);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);

  const persistWorkflow = useCallback(
    (nextNodes: Node[], nextEdges: Edge[]) => {
      if (!id) return;
      dispatch(
        updateWorkflowRequest({
          campaignId: id,
          draftFlow: {
            nodes: nextNodes,
            edges: nextEdges,
          },
        })
      );
    },
    [id, dispatch]
  );
  const applyLayout = useCallback(
    (nextNodes: Node[], nextEdges: Edge[]) => {
      const { nodes: layoutedNodes, edges: layoutedEdges } =
        getLayoutedElements(nextNodes, nextEdges, 'TB');
      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
      return { layoutedNodes, layoutedEdges };
    },
    [setNodes, setEdges]
  );

  // Load workflow data when available
  useEffect(() => {
    if (workflow && workflow.draftFlow) {
      if (
        workflow.draftFlow.nodes &&
        workflow.draftFlow.nodes.length > 0 &&
        workflow.draftFlow.edges
      ) {
        applyLayout(workflow.draftFlow.nodes, workflow.draftFlow.edges);
      }
    }
  }, [workflow, applyLayout]);

  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const [addAfterNode, setAddAfterNode] = useState<{
    nodeId: string;
    branch?: 'true' | 'false' | null;
  } | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const clearReactFlowSelection = useCallback(() => {
    setNodes((nds: Node[]) =>
      nds.some((node) => node.selected)
        ? nds.map((node: Node) =>
            node.selected ? { ...node, selected: false } : node
          )
        : nds
    );
  }, [setNodes]);

  // Common conditions state - only one panel can be open at a time
  const [nodeConditions, setNodeConditions] = useState<{
    [key: string]: any[];
  }>({});
  const [nodeConditionsModified, setNodeConditionsModified] = useState(false);

  // Check if trigger node has conditions
  const hasTriggerConditions = useMemo(() => {
    const triggerNode = nodes.find((node) => node.data?.type === 'trigger');
    if (!triggerNode) return false;

    const conditions = (triggerNode.data?.config as any)?.conditions || {};
    return (
      Object.keys(conditions).length > 0 &&
      (conditions.and?.length > 0 || conditions.or?.length > 0)
    );
  }, [nodes]);

  const isAttributeSegmentTrigger = useMemo(() => {
    const triggerNode = nodes.find((node) => node.data?.type === 'trigger');
    const triggerType = (triggerNode?.data?.config as any)?.trigger;
    return triggerType !== 'event';
  }, [nodes]);

  const createEdge = useCallback(
    (
      sourceId: string,
      targetId: string,
      options?: { sourceHandle?: string; branch?: 'true' | 'false' }
    ) => {
      if (sourceId === targetId) return null;

      const existingEdge = edges.find(
        (edge) =>
          edge.source === sourceId &&
          edge.target === targetId &&
          edge.sourceHandle === options?.sourceHandle
      );

      if (existingEdge) return null;

      return {
        id: uuidv4(),
        source: sourceId,
        target: targetId,
        type: 'custom',
        sourceHandle: options?.sourceHandle,
        data: options?.branch ? { branch: options.branch } : undefined,
      };
    },
    [edges]
  );

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const addNewNode = useCallback(
    (nodeType: string, config: any, sourceId?: string, targetId?: string) => {
      const newNode: Node<any, string | undefined> = {
        id: uuidv4(),
        type: 'action',
        position: { x: 0, y: 0 },
        data: {
          label: config.name || 'New Node',
          description: config.description || '',
          icon: config.icon,
          color: config.color,
          status: 'draft',
          type: nodeType,
          config: {
            conditions: {},
            inputs: {},
            outputs: {},
            settings: {},
          },
        },
      };
      if (selectedEdge) {
        const edgeBranch = (
          selectedEdge.data as { branch?: 'true' | 'false' } | undefined
        )?.branch;
        const newEdge1 = createEdge(selectedEdge.source, newNode.id, {
          sourceHandle: selectedEdge.sourceHandle ?? undefined,
          branch: edgeBranch,
        });
        const newEdge2 = createEdge(newNode.id, selectedEdge.target);
        if (newEdge1 && newEdge2) {
          const nextEdges = edges.filter(
            (edge: Edge) => edge.id !== selectedEdge.id
          );
          const updatedEdges = [...nextEdges, newEdge1, newEdge2];
          const updatedNodes = [...nodes, newNode];
          const { layoutedNodes, layoutedEdges } = applyLayout(
            updatedNodes,
            updatedEdges
          );
          persistWorkflow(layoutedNodes, layoutedEdges);
          setSelectedEdge(null);
        }
      } else {
        const targetSourceId = sourceId || addAfterNode?.nodeId;
        const targetBranch = addAfterNode?.branch ?? undefined;
        const lastEdge = edges.length > 0 ? edges[edges.length - 1] : null;
        const lastEdgeTarget = lastEdge?.target;
        const fallbackLastNode =
          nodes.length > 1
            ? nodes.find((node) => node.id === lastEdgeTarget)
            : nodes[0];
        const sourceNode = targetSourceId
          ? nodes.find((node) => node.id === targetSourceId)
          : fallbackLastNode;
        const newEdge = createEdge(sourceNode?.id || '', newNode.id, {
          sourceHandle: targetBranch,
          branch: targetBranch,
        });
        if (newEdge) {
          const filteredEdges = edges.filter(
            (edge: Edge) =>
              !(
                edge.source === sourceNode?.id &&
                edge.target === newNode.id &&
                edge.sourceHandle === targetBranch
              )
          );
          const updatedEdges = [...filteredEdges, newEdge];
          const updatedNodes = [...nodes, newNode];
          const { layoutedNodes, layoutedEdges } = applyLayout(
            updatedNodes,
            updatedEdges
          );
          persistWorkflow(layoutedNodes, layoutedEdges);
          setAddAfterNode(null);
        }
      }
    },
    [
      createEdge,
      selectedEdge,
      nodes,
      edges,
      persistWorkflow,
      addAfterNode,
      applyLayout,
    ]
  );

  const deleteSelectedNode = useCallback(() => {
    if (!selectedNode || selectedNode.data?.isDefault) {
      return;
    }

    // Condition node: remove the node and every descendant on both branches.
    if (selectedNode.data?.type === 'true-false') {
      const idsToRemove = getReachableDescendantIds(selectedNode.id, edges);
      const updatedEdges = edges.filter(
        (e) => !idsToRemove.has(e.source) && !idsToRemove.has(e.target)
      );
      const updatedNodes = nodes.filter((node) => !idsToRemove.has(node.id));
      const { layoutedNodes, layoutedEdges } = applyLayout(
        updatedNodes,
        updatedEdges
      );

      setSelectedNode(null);
      clearReactFlowSelection();

      persistWorkflow(layoutedNodes, layoutedEdges);
      return;
    }

    const aboveEdge = edges.find((edge) => edge.target === selectedNode.id);
    const belowEdge = edges.find((edge) => edge.source === selectedNode.id);

    let updatedEdges = edges;

    const newEdge = createEdge(
      aboveEdge?.source || '',
      belowEdge?.target || ''
    );
    const filteredEdges = edges.filter(
      (e) => e.id !== aboveEdge?.id && e.id !== belowEdge?.id
    );
    updatedEdges = newEdge ? [...filteredEdges, newEdge] : filteredEdges;
    const updatedNodes = nodes.filter((node) => node.id !== selectedNode.id);
    const { layoutedNodes, layoutedEdges } = applyLayout(
      updatedNodes,
      updatedEdges
    );

    setSelectedNode(null);
    clearReactFlowSelection();

    persistWorkflow(layoutedNodes, layoutedEdges);
  }, [
    selectedNode,
    createEdge,
    clearReactFlowSelection,
    nodes,
    edges,
    persistWorkflow,
    applyLayout,
  ]);

  const updateNodeConfig = useCallback(
    (nodeId: string, config: Record<string, any>) => {
      setNodes((nds) => {
        const updatedNodes = nds.map((node) => {
          if (node.id === nodeId) {
            return {
              ...node,
              data: {
                ...node.data,
                config: {
                  ...(node.data.config || {}),
                  ...config,
                },
              },
            };
          }
          return node;
        });

        // Debounce auto-save to avoid high-frequency backend writes.
        if (id) {
          if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
          }
          saveTimeoutRef.current = setTimeout(() => {
            persistWorkflow(updatedNodes, edgesRef.current);
          }, 250);
        }

        return updatedNodes;
      });
    },
    [setNodes, id, persistWorkflow]
  );

  const validateWorkflow = useCallback(() => {
    const nodeIds = nodes.map((node) => node.id);
    const connectedNodes = new Set<string>();

    edges.forEach((edge) => {
      connectedNodes.add(edge.source);
      connectedNodes.add(edge.target);
    });

    const disconnectedNodes = nodeIds.filter((id) => {
      const node = nodes.find((n) => n.id === id);
      if (node?.data?.type === 'trigger') {
        return false;
      }
      return !connectedNodes.has(id);
    });

    return {
      isValid: disconnectedNodes.length === 0,
      disconnectedNodes,
    };
  }, [nodes, edges]);

  useEffect(() => {
    const validation = validateWorkflow();
    if (!validation.isValid) {
      console.warn('Workflow validation failed:', validation.disconnectedNodes);
    }
  }, [nodes, edges, validateWorkflow]);

  useEffect(() => {
    if (workflowError) {
      showToast({
        type: 'error',
        title: 'Error',
        message: workflowError,
      });
      dispatch(clearWorkflowState());
    }
  }, [workflowError, showToast, dispatch]);

  const [prevPublishLoading, setPrevPublishLoading] = useState(false);

  useEffect(() => {
    if (prevPublishLoading && !publishLoading && !workflowError) {
      showToast({
        type: 'success',
        title: 'Success',
        message: 'Campaign is live now!',
      });
    }
    setPrevPublishLoading(publishLoading);
  }, [publishLoading, workflowError, showToast, prevPublishLoading]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedEdge(null);
        setSelectedNode(null);
        setAddAfterNode(null);
        setReviewPanelMode(null);
        clearReactFlowSelection();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [clearReactFlowSelection]);

  useEffect(() => {
    const handleAddNodeAfterNode = (event: Event) => {
      const customEvent = event as CustomEvent<{
        nodeId: string;
        branch?: 'true' | 'false' | null;
      }>;
      const nodeId = customEvent.detail?.nodeId;
      if (!nodeId) return;
      setAddAfterNode({
        nodeId,
        branch: customEvent.detail?.branch || null,
      });
      setSelectedNode(null);
      setSelectedEdge(null);
      clearReactFlowSelection();
    };

    window.addEventListener(
      'addNodeAfterNode',
      handleAddNodeAfterNode as EventListener
    );

    return () => {
      window.removeEventListener(
        'addNodeAfterNode',
        handleAddNodeAfterNode as EventListener
      );
    };
  }, [clearReactFlowSelection]);

  useEffect(() => {
    const handleAddNodeFromEdge = (event: Event) => {
      const customEvent = event as CustomEvent<{ edgeId: string }>;
      const edgeId = customEvent.detail?.edgeId;
      if (!edgeId) return;
      const edge = edges.find((item) => item.id === edgeId) || null;
      setSelectedEdge(edge);
      setSelectedNode(null);
      setAddAfterNode(null);
      clearReactFlowSelection();
    };

    window.addEventListener(
      'addNodeFromEdge',
      handleAddNodeFromEdge as EventListener
    );

    return () => {
      window.removeEventListener(
        'addNodeFromEdge',
        handleAddNodeFromEdge as EventListener
      );
    };
  }, [edges, clearReactFlowSelection]);

  const renderNodes = useMemo(() => {
    const sourceNodeIds = new Set(edges.map((edge) => edge.source));
    const trueBranchSources = new Set(
      edges
        .filter((edge) => edge.sourceHandle === 'true')
        .map((edge) => edge.source)
    );
    const falseBranchSources = new Set(
      edges
        .filter((edge) => edge.sourceHandle === 'false')
        .map((edge) => edge.source)
    );
    return nodes.map((node) => ({
      ...node,
      data: {
        ...(node.data || {}),
        isTerminal: !sourceNodeIds.has(node.id),
        trueBranchTerminal: !trueBranchSources.has(node.id),
        falseBranchTerminal: !falseBranchSources.has(node.id),
        isAddTarget: addAfterNode?.nodeId === node.id,
        activeAddBranch:
          addAfterNode?.nodeId === node.id ? addAfterNode.branch || null : null,
        mergeIncomingBranches: nodeHasMergeIncomingBranches(
          node.id,
          nodes,
          edges
        ),
      },
    }));
  }, [nodes, edges, addAfterNode]);

  const edgeTypes = useMemo(() => ({ custom: CustomEdge }), []);

  const handleFitView = useCallback(() => {
    if (!reactFlowInstance) return;
    reactFlowInstance.fitView({
      padding: 0.5,
      minZoom: 0.5,
      maxZoom: 1.5,
      duration: 300,
    });
  }, [reactFlowInstance]);

  const handleFormatFlow = useCallback(() => {
    const { layoutedNodes, layoutedEdges } = applyLayout(nodes, edges);
    persistWorkflow(layoutedNodes, layoutedEdges);
    setTimeout(() => {
      handleFitView();
    }, 120);
  }, [applyLayout, nodes, edges, persistWorkflow, handleFitView]);

  useEffect(() => {
    if (!loading && nodes.length > 0) {
      const timer = setTimeout(() => {
        handleFitView();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [loading, nodes.length, handleFitView]);

  const onSelectionChange = useCallback((selection: any) => {
    const selectedNodeData = selection.nodes[0] as Node;
    setSelectedNode(selectedNodeData);
    setSelectedEdge((selection.edges[0] as Edge) || null);
    setAddAfterNode(null);
    if (selectedNodeData) {
      setReviewPanelMode(null);
    }

    // Initialize conditions for any node type
    if (selectedNodeData?.data?.type) {
      const existingConditions =
        (selectedNodeData.data?.config as any)?.conditions || {};
      setNodeConditions(existingConditions);
      setNodeConditionsModified(false);
    }
  }, []);

  const onPaneClick = useCallback(() => {
    setReviewPanelMode(null);
    setSelectedNode(null);
    setSelectedEdge(null);
    setAddAfterNode(null);
    clearReactFlowSelection();
  }, [clearReactFlowSelection]);

  const connectionLineStyle = useMemo(
    () => ({
      stroke: 'hsl(var(--primary))',
      strokeWidth: 2,
    }),
    []
  );

  const nodeColor = useCallback((node: any) => {
    switch (node.type) {
      case 'action':
        return 'hsl(var(--primary))';
      default:
        return 'hsl(var(--muted-foreground))';
    }
  }, []);

  const handleEditClick = useCallback(() => {
    setIsEditModalOpen(true);
  }, []);

  const handleSaveCampaign = useCallback(
    async (title: string, description: string) => {
      setIsUpdating(true);
      try {
        dispatch(updateCampaignRequest(id || '', { name: title, description }));
      } catch (error) {
        console.error('Error updating campaign:', error);
        showToast({
          type: 'error',
          title: 'Error',
          message: 'Failed to update campaign',
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [id, showToast, dispatch]
  );

  const handlePublishWorkflow = useCallback(() => {
    if (!id) return;

    // Validate workflow before publishing
    const validation = validateWorkflow();
    if (!validation.isValid) {
      showToast({
        type: 'error',
        title: 'Validation Error',
        message: 'Please ensure all nodes are connected before publishing.',
      });
      return;
    }

    // If campaign is draft, show review modal first
    if (campaign?.status === 'draft') {
      setIsReviewModalOpen(true);
      return;
    }

    // Publish the campaign (for non-draft campaigns, don't pass type if already live)
    dispatch(publishCampaignRequest(id));
  }, [id, dispatch, validateWorkflow, showToast, campaign?.status]);

  const handleStartCampaignFromReview = useCallback(
    (selectedType?: 'all_contacts' | 'future_contacts') => {
      if (!id) return;
      dispatch(publishCampaignRequest(id, selectedType));
      setIsReviewModalOpen(false);
    },
    [id, dispatch]
  );

  const handleSwitchToDashboard = useCallback(
    (screen: 'dashboard' | 'editor') => {
      setViewMode(screen);
    },
    []
  );

  // Render loading state
  if (loading) {
    return <WorkflowCanvasSkeleton />;
  }

  // Render error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-background">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Error Loading Campaign
          </h3>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  // Render campaign not found
  if (!campaign) {
    return (
      <div className="flex items-center justify-center h-full bg-background">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Campaign Not Found
          </h3>
          <p className="text-muted-foreground">
            The requested campaign could not be found.
          </p>
        </div>
      </div>
    );
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'live':
        return 'Running';
      case 'paused':
        return 'Paused';
      case 'archived':
        return 'Archived';
      default:
        return 'Draft';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'live':
        return <CheckCircle2 className="w-4 h-4 text-success" />;
      case 'paused':
        return <Pause className="w-4 h-4 text-warning" />;
      case 'archived':
        return <Clock className="w-4 h-4 text-muted-foreground" />;
      default:
        return <FileText className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <WorkflowErrorBoundary>
      <div className={cn('w-full h-full', className)}>
        {/* Campaign Status Header */}
        {campaign.status === 'live' && (
          <div className="bg-card border-b border-border px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                    <Mail className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold text-foreground">
                      {campaign.name}
                    </h1>
                    <div className="flex items-center space-x-2 mt-1">
                      <div
                        className={cn(
                          'flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium',
                          campaign.status === 'live'
                            ? 'bg-success-background text-success'
                            : 'bg-muted text-muted-foreground'
                        )}
                      >
                        {getStatusIcon(campaign.status)}
                        <span>{getStatusText(campaign.status)}</span>
                      </div>
                      <span className="text-sm text-muted-foreground">•</span>
                      <span className="text-sm text-muted-foreground">
                        Last saved {formatDate(campaign.updatedAt)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleSwitchToDashboard('dashboard')}
                    className={cn(
                      'px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                      viewMode === 'dashboard'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground hover:bg-muted/80'
                    )}
                  >
                    Dashboard
                  </button>
                  <button
                    onClick={() => handleSwitchToDashboard('editor')}
                    className={cn(
                      'px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                      viewMode === 'editor'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground hover:bg-muted/80'
                    )}
                  >
                    Edit Workflow
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        {viewMode === 'dashboard' && campaign.status === 'live' ? (
          <CampaignDashboard
            className="h-[calc(95vh-100px)]"
            campaign={campaign}
            onEditWorkflow={() => handleSwitchToDashboard('editor')}
            onPauseCampaign={() => {
              // TODO: Implement pause campaign functionality
              console.log('Pause campaign');
            }}
            onResumeCampaign={() => {
              // TODO: Implement resume campaign functionality
              console.log('Resume campaign');
            }}
          />
        ) : (
          <ReactFlow
            nodes={renderNodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onInit={setReactFlowInstance}
            onSelectionChange={onSelectionChange}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onlyRenderVisibleElements
            fitView
            fitViewOptions={{
              padding: 0.5,
              minZoom: 0.5,
              maxZoom: 1.5,
              duration: 1000,
            }}
            minZoom={0.5}
            maxZoom={1.5}
            snapToGrid
            nodesDraggable={false}
            elementsSelectable={true}
            connectionLineStyle={connectionLineStyle}
            deleteKeyCode={null}
          >
            <Controls />
            <Background color="hsl(var(--border))" gap={16} />
            <MiniMap
              nodeColor={nodeColor}
              nodeStrokeWidth={3}
              zoomable
              pannable
            />

            <Panel position="top-left" className="">
              <LeftSidePanel
                campaign={campaign || undefined}
                loading={loading}
                error={error}
                onEditClick={handleEditClick}
              />
            </Panel>

            <Panel position="top-right" className="">
              <div className="bg-card rounded-lg border border-border shadow-sm p-2 space-y-3">
                <div className="w-full flex gap-2 flex-wrap">
                  <>
                    {reviews?.errors && reviews.errors.length > 0 && (
                      <button
                        onClick={() => {
                          setReviewPanelMode(
                            reviewPanelMode === 'errors' ? null : 'errors'
                          );
                          setSelectedNode(null);
                          clearReactFlowSelection();
                        }}
                        disabled={reviewLoading}
                        className="whitespace-nowrap border border-primary rounded-lg text-primary py-2 px-4 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/10"
                      >
                        Review Errors ({reviews.errors.length})
                      </button>
                    )}
                    {reviews?.warnings && reviews.warnings.length > 0 && (
                      <button
                        onClick={() => {
                          setReviewPanelMode(
                            reviewPanelMode === 'warnings' ? null : 'warnings'
                          );
                          setSelectedNode(null);
                          clearReactFlowSelection();
                        }}
                        disabled={reviewLoading}
                        className="whitespace-nowrap border border-amber-500/60 rounded-lg text-amber-700 dark:text-amber-400 py-2 px-4 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-amber-500/10"
                      >
                        Warnings ({reviews.warnings.length})
                      </button>
                    )}
                    <button
                      onClick={handleFormatFlow}
                      disabled={updateLoading || reviewLoading}
                      className="whitespace-nowrap border border-border rounded-lg text-foreground py-2 px-4 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted/40"
                    >
                      Format Flow
                    </button>
                    <button
                      onClick={handlePublishWorkflow}
                      disabled={
                        publishCampaignLoading ||
                        updateLoading ||
                        reviewLoading ||
                        !reviews?.isValid
                      }
                      className="w-full whitespace-nowrap bg-primary text-primary-foreground py-2 px-4 rounded-lg text-sm font-medium hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {publishCampaignLoading ? (
                        <div className="flex items-center justify-center">
                          <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4 text-primary-foreground" />
                          Publishing...
                        </div>
                      ) : campaign?.status === 'live' ? (
                        'Update Campaign'
                      ) : (
                        'Start Campaign'
                      )}
                    </button>
                  </>
                </div>
              </div>
            </Panel>

            <Panel position="top-right" style={{ top: '80px' }}>
              {!selectedNode && !reviewPanelMode && (
                <RightSidePanel
                  addNewNode={addNewNode}
                  hasTriggerConditions={hasTriggerConditions}
                  isAttributeSegmentTrigger={isAttributeSegmentTrigger}
                />
              )}
              {/* {selectedNode && selectedNode.data?.type === 'trigger' && (
                <TriggerPanel
                  onClose={() => {
                    setSelectedNode(null);
                    setNodeConditionsModified(false);
                    clearReactFlowSelection();
                  }}
                  onSave={(data) => {
                    if (selectedNode) {
                      updateNodeConfig(selectedNode.id, data);
                      setNodes((nds) =>
                        nds.map((node) => {
                          if (node.id === selectedNode.id) {
                            return {
                              ...node,
                              data: {
                                ...node.data,
                                status: data.status || 'configured',
                              },
                            };
                          }
                          return node;
                        })
                      );

                      setNodeConditions(data.conditions || {});
                      setNodeConditionsModified(false);
                      setSelectedNode(null);
                      clearReactFlowSelection();

                      // Show success message
                      showToast({
                        type: 'success',
                        title: 'Trigger Updated',
                        message:
                          'Trigger conditions have been saved successfully!',
                      });
                    }
                  }}
                  conditions={nodeConditions}
                  onConditionsChange={(conditions: {
                    [key: string]: any[];
                  }) => {
                    setNodeConditions(conditions);
                    setNodeConditionsModified(true);
                  }}
                  conditionsModified={nodeConditionsModified}
                  campaignId={id}
                />
              )} */}
              {selectedNode && selectedNode.data?.type === 'email' && (
                <EmailPanel
                  onClose={() => {
                    setSelectedNode(null);
                    setNodeConditionsModified(false);
                    clearReactFlowSelection();
                  }}
                  onSave={(data) => {
                    if (selectedNode) {
                      updateNodeConfig(selectedNode.id, data);
                      setNodeConditions(data.conditions || {});
                      setNodeConditionsModified(false);
                      setSelectedNode(null);
                      clearReactFlowSelection();
                      showToast({
                        type: 'success',
                        title: 'Email Updated',
                        message:
                          'Email configuration has been saved successfully!',
                      });
                    }
                  }}
                  conditions={nodeConditions}
                  onConditionsChange={(conditions: {
                    [key: string]: any[];
                  }) => {
                    setNodeConditions(conditions);
                    setNodeConditionsModified(true);
                  }}
                  conditionsModified={nodeConditionsModified}
                  campaignId={id}
                  selectedNode={selectedNode}
                />
              )}
              {selectedNode && selectedNode.data?.type === 'time-delay' && (
                <DelayPanel
                  onClose={() => {
                    setSelectedNode(null);
                    setNodeConditionsModified(false);
                    clearReactFlowSelection();
                  }}
                  onSave={(data) => {
                    if (selectedNode) {
                      updateNodeConfig(selectedNode.id, data);
                      setNodeConditions(data.conditions || {});
                      setNodeConditionsModified(false);
                      setSelectedNode(null);
                      clearReactFlowSelection();

                      // Show success message
                      showToast({
                        type: 'success',
                        title: 'Delay Updated',
                        message:
                          'Delay configuration has been saved successfully!',
                      });
                    }
                  }}
                  conditions={nodeConditions}
                  onConditionsChange={(conditions: {
                    [key: string]: any[];
                  }) => {
                    setNodeConditions(conditions);
                    setNodeConditionsModified(true);
                  }}
                  conditionsModified={nodeConditionsModified}
                  initialDelayValue={
                    (selectedNode.data?.config as any)?.delay?.value || 0
                  }
                  initialDelayUnit={
                    (selectedNode.data?.config as any)?.delay?.unit || 'days'
                  }
                />
              )}
              {selectedNode && selectedNode.data?.type === 'true-false' && (
                <TrueFalsePanel
                  onClose={() => {
                    setSelectedNode(null);
                    clearReactFlowSelection();
                  }}
                  onSave={(data) => {
                    if (selectedNode) {
                      updateNodeConfig(selectedNode.id, data);
                      clearReactFlowSelection();

                      // Show success message
                      showToast({
                        type: 'success',
                        title: 'Condition Updated',
                        message:
                          'Condition configuration has been saved successfully!',
                      });
                    }
                  }}
                  conditions={
                    (selectedNode.data?.config as any)?.conditions || {}
                  }
                />
              )}
              {reviewPanelMode && (
                <ReviewErrorsPanel
                  variant={reviewPanelMode}
                  onClose={() => setReviewPanelMode(null)}
                  campaignId={id || ''}
                  onViewMessage={(nodeId) => {
                    const node = nodes.find((node: any) => node.id === nodeId);
                    if (node) {
                      setSelectedNode(node);
                      setReviewPanelMode(null);
                    }
                  }}
                />
              )}
            </Panel>

            <Panel position="bottom-center" className="mb-4">
              <CenterPanel
                selectedNode={selectedNode}
                selectedEdge={selectedEdge}
                deleteSelectedNode={deleteSelectedNode}
              />
            </Panel>
          </ReactFlow>
        )}

        {/* Edit Campaign Modal */}
        <EditCampaignModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleSaveCampaign}
          initialTitle={campaign?.name || ''}
          initialDescription={campaign?.description || ''}
          loading={isUpdating}
        />

        {/* Review Campaign Modal */}
        <ReviewCampaignModal
          isOpen={isReviewModalOpen}
          onClose={() => setIsReviewModalOpen(false)}
          onStartCampaign={handleStartCampaignFromReview}
          campaign={campaign}
          nodes={nodes}
          loading={publishCampaignLoading}
        />
      </div>
    </WorkflowErrorBoundary>
  );
};

export default WorkflowCanvas;
